import React, { useState } from 'react';
import { useForm } from '../context/FormContext';
import { US_STATES } from '../utils/states';
import { currentMonthYear, yearsAgoMonthYear, formatMonthYear, monthsDiff, requiredHistoryYears,
         schoolTerminatorValid, ageAtMonth, SCHOOL_MAX_END_AGE } from '../utils/validation';

const emptyEntry = (startDate = '', endDate = '') => ({
  employer_name: '', street: '', city: '', state: '', zip: '',
  start_date: startDate, end_date: endDate, position: '', dot_regulated: null,
  is_school: false, is_non_working: false, reason: ''
});

// A school entry is a TERMINATOR, not a job. It carries no employer, city,
// dates, position, or DOT status. Its only job is to close out the history.
const schoolEntry = (endDate = '') => ({
  employer_name: 'School', street: '', city: '', state: '', zip: '',
  start_date: '', end_date: endDate, position: 'School', dot_regulated: false,
  is_school: true, is_non_working: false, reason: ''
});

// A non-working entry (unemployment, disability, medical leave, personal time)
// is a NORMAL LINK IN THE CHAIN, not a terminator. Unlike school, it keeps its
// dates and participates fully in the ripple + gap/coverage logic, and more
// entries can follow it. The only visible fields are a free-text reason and the
// start date; everything else is defaulted (no employer/city/state/position, and
// dot_regulated forced false). end_date is preserved so the ripple stays intact.
const nonWorkingEntry = (startDate = '', endDate = '') => ({
  employer_name: '', street: '', city: '', state: '', zip: '',
  start_date: startDate, end_date: endDate, position: '', dot_regulated: false,
  is_school: false, is_non_working: true, reason: ''
});

export default function Step22_JobEntries({ onNext, onBack }) {
  const { state, dispatch } = useForm();
  const [errs, setErrs] = useState([]);

  // Initialize with one entry if empty.
  const rawEntries = state.jobEntries.length > 0 ? state.jobEntries : [emptyEntry('', currentMonthYear())];
  // INVARIANT: the first (most-recent) entry ALWAYS ends in the current month --
  // the application is current as of today. Enforced here (not just at init) so an
  // edited or previously-saved form can never leave it blank or stale. Applies to
  // jobs, non-working periods, and school alike.
  const entries = rawEntries.map((e, i) => i === 0 ? { ...e, end_date: currentMonthYear() } : e);

  const setEntries = (updated) => dispatch({ type: 'SET_JOB_ENTRIES', value: updated });

  const setField = (idx, field, val) => {
    const updated = entries.map((e, i) => i === idx ? { ...e, [field]: val } : e);

    // RIPPLE: end_date is never typed by the driver -- it mirrors the start_date
    // of the NEWER entry above it. Entries run newest (idx 0) -> oldest, so the
    // job at idx+1 is the OLDER one, and it ENDED when the job at idx BEGAN.
    // Therefore editing entry idx's start_date must push that value down into
    // entry idx+1's end_date. Without this, a driver who goes back and corrects
    // a start date leaves a stale end date below it and an invisible gap.
    if (field === 'start_date' && idx + 1 < updated.length) {
      updated[idx + 1] = { ...updated[idx + 1], end_date: val };
    }

    setEntries(updated);
    setErrs(prev => { const n = [...prev]; if (n[idx]) { const c = { ...n[idx] }; delete c[field]; n[idx] = c; } return n; });
  };

  // Checking the school box swaps the whole entry object, so no stale job fields
  // (city/state/position/DOT) linger behind the collapsed card. The end_date is
  // preserved -- it still mirrors the start of the entry above it.
  const toggleSchool = (idx, checked) => {
    const updated = entries.map((e, i) => {
      if (i !== idx) return e;
      return checked ? schoolEntry(e.end_date) : emptyEntry('', e.end_date);
    });
    setEntries(updated);
    setErrs(prev => { const n = [...prev]; n[idx] = {}; return n; });
  };

  // Non-working toggle. Unlike school, this entry KEEPS its dates and stays a
  // normal link in the chain, so we preserve BOTH start_date and end_date when
  // switching in either direction -- the ripple depends on them staying intact.
  const toggleNonWorking = (idx, checked) => {
    const updated = entries.map((e, i) => {
      if (i !== idx) return e;
      return checked
        ? nonWorkingEntry(e.start_date, e.end_date)
        : emptyEntry(e.start_date, e.end_date);
    });
    setEntries(updated);
    setErrs(prev => { const n = [...prev]; n[idx] = {}; return n; });
  };

  const addEntry = () => {
    const lastStart = entries[entries.length - 1]?.start_date || currentMonthYear();
    setEntries([...entries, emptyEntry('', lastStart)]);
  };

  const removeEntry = (idx) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((_, i) => i !== idx));
    setErrs(prev => prev.filter((_, i) => i !== idx));
  };

  // How far back this driver must report. 10 years, OR back to age 18,
  // whichever is shorter. (DOB captured earlier in the wizard.)
  const requiredYears = requiredHistoryYears(state.dateOfBirth);
  const requiredBackTo = yearsAgoMonthYear(requiredYears);

  const earliestEntry = entries.length > 0 ? entries[entries.length - 1] : null;
  const earliestStart = earliestEntry ? earliestEntry.start_date : '';

  // A school entry terminates the history. It can only be the last card (Add
  // Another is hidden once one exists), but scan the whole list so a mid-edit
  // state can never strand the driver.
  const hasSchoolEntry = entries.some(e => e.is_school);

  // The school entry's end_date is not typed by the driver -- the ripple pins it
  // to the start of the entry above, or to the current month when school is the
  // sole entry. It is therefore an honest read of when the driver is claiming
  // school ended, and it is what the terminator rule is judged on.
  const schoolEntryObj = entries.find(e => e.is_school) || null;
  const schoolEndMonth = schoolEntryObj ? schoolEntryObj.end_date : '';
  const schoolAgeAtEnd = schoolEntryObj ? ageAtMonth(state.dateOfBirth, schoolEndMonth) : null;

  // 🔑 A school entry TERMINATES the history only when it is credibly the START
  // of a working life -- i.e. school ended while the driver was still young
  // enough for there to have been nothing before it. A driver who left school
  // at 36 was not "in school beforehand"; he owes the years since.
  //
  // Before this gate existed, ticking the box was an unconditional pass and
  // wiped the entire span requirement on a blank form. Two applications were
  // filed that way with no employment history at all.
  const schoolTerminates = hasSchoolEntry && schoolTerminatorValid(state.dateOfBirth, schoolEndMonth);

  // The required span is satisfied two ways:
  //  1. The earliest entry reaches back to the required date, OR
  //  2. A school entry exists AND it is entitled to terminate (see above).
  const spanCovered = earliestStart && earliestStart <= requiredBackTo;
  const historySatisfied = requiredYears === 0 || spanCovered || schoolTerminates;

  // Ticked the box, but not entitled to it. Drives the redirect message.
  const schoolRejected = hasSchoolEntry && !schoolTerminates && !spanCovered && requiredYears > 0;

  // Detect gaps between entries
  const detectGaps = (ents) => {
    const gaps = [];
    for (let i = 0; i < ents.length - 1; i++) {
      // School has no start date and ends the history -- never a gap boundary.
      if (ents[i + 1].is_school) continue;
      const currentEnd = ents[i].end_date || ents[i].start_date;
      const nextStart = ents[i + 1].start_date;
      if (currentEnd && nextStart) {
        const diff = monthsDiff(nextStart, currentEnd);
        if (diff > 1) gaps.push(i + 1); // gap before entry i+1
      }
    }
    return gaps;
  };

  const validate = () => {
    const newErrs = entries.map((e, i) => {
      const err = {};
      // A school entry is a terminator, not a job. It requires nothing.
      if (e.is_school) return err;
      // A non-working entry needs only a reason plus dates -- no employer,
      // city, state, position, or DOT selection.
      if (e.is_non_working) {
        if (!e.reason.trim()) err.reason = 'Please say what this was (e.g. unemployment, medical leave)';
        if (!e.start_date) err.start_date = 'Start date required';
        if (i > 0 && !e.end_date) err.end_date = 'End date required';
        if (e.start_date && e.end_date && e.start_date >= e.end_date) err.start_date = 'Start must be before end date';
        return err;
      }
      if (!e.employer_name.trim()) err.employer_name = 'Employer name required';
      if (!e.city.trim()) err.city = 'City required';
      if (!e.state) err.state = 'State required';
      if (!e.start_date) err.start_date = 'Start date required';
      if (i > 0 && !e.end_date) err.end_date = 'End date required';
      if (!e.position.trim()) err.position = 'Position required';
      if (e.dot_regulated === null) err.dot_regulated = 'DOT/Non-DOT selection required';
      if (e.start_date && e.end_date && e.start_date >= e.end_date) err.start_date = 'Start must be before end date';
      return err;
    });
    setErrs(newErrs);
    return newErrs.every(e => Object.keys(e).length === 0);
  };

  const next = () => {
    if (!validate()) return;
    if (!historySatisfied) {
      if (schoolRejected) {
        // Redirect, not reject. Most drivers who land here are honest men who
        // took the fastest route off the page. Tell them what to do instead.
        alert(
          `Your history needs to reach back to ${formatMonthYear(requiredBackTo)}.\n\n` +
          `You have marked school as ending ${formatMonthYear(schoolEndMonth)}` +
          (schoolAgeAtEnd !== null ? `, when you were ${schoolAgeAtEnd}` : '') +
          `, so school cannot be the start of your work history — please tell us ` +
          `what you were doing before then.\n\n` +
          `If you attended school or training during this period, uncheck the ` +
          `school box and use "This was a non-working period" instead. That lets ` +
          `you enter the dates you were in school and keep going further back.`
        );
        return;
      }
      alert(
        `Please add entries going back to ${formatMonthYear(requiredBackTo)}, ` +
        `or add a final entry and check "Before this, I was in school" if you ` +
        `have not been working that entire time.`
      );
      return;
    }
    onNext();
  };

  const gaps = detectGaps(entries);

  return (
    <div>
      <div className="step-card">
        <h2 className="step-title">Employment History</h2>
        <p className="step-subtitle">
          Start with your most recent position and work backward. If your history does not go
          back far enough because you were in school, add one final entry and check the box
          marked <strong>"Before this, I was in school."</strong> That completes your history.
        </p>

        {historySatisfied && (
          <div style={{ background: '#f0fff4', border: '1px solid var(--success)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 14, color: '#276749' }}>
            ✅ Employment history requirement met. You may continue.
          </div>
        )}
      </div>

      {entries.map((entry, idx) => {
        const err = errs[idx] || {};
        const isFirst = idx === 0;
        const hasGapBefore = gaps.includes(idx);
        return (
          <div key={idx}>
            {hasGapBefore && (
              <div className="warning-banner">
                ⚠️ A gap has been detected before this entry. Please add an entry to explain the period between these jobs (unemployment, disability, personal time, etc.)
              </div>
            )}
            <div className="job-entry-card">
              <div className="job-entry-header">
                <span className="job-entry-num">Entry {idx + 1} {isFirst ? '(Most Recent)' : ''}</span>
                {!isFirst && <button className="btn-danger" onClick={() => removeEntry(idx)}>Remove</button>}
              </div>

              {/* School checkbox -- offered on EVERY entry, including the first.
                  A straight-out-of-school CDL driver can have school as the sole entry:
                  it terminates the history even at position one. Hidden when this card
                  is already marked non-working (mutually exclusive). */}
              {!entry.is_non_working && (
                <div className="field-group" style={{
                  background: '#f4f6f9', border: '1px solid var(--border)', borderRadius: 8,
                  padding: 12, marginBottom: entry.is_school ? 0 : 16
                }}>
                  <label className="radio-option" style={{ margin: 0, alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={!!entry.is_school}
                      onChange={e => toggleSchool(idx, e.target.checked)}
                      style={{ marginTop: 3 }} />
                    <span style={{ marginLeft: 8 }}>
                      <strong>Before this, I was in school.</strong>
                      <div className="field-hint" style={{ marginTop: 4 }}>
                        Check this if your work history does not go back far enough because you
                        were in school (high school, GED, trade school, or college). Nothing
                        further is required.
                      </div>
                    </span>
                  </label>
                </div>
              )}

              {/* Non-working checkbox -- offered on EVERY entry, including the first.
                  A driver whose most recent status is unemployment/disability/medical
                  leave can mark the first card non-working. Collapses the card to a
                  reason + dates. Hidden once school is checked (mutually exclusive). */}
              {!entry.is_school && (
                <div className="field-group" style={{
                  background: '#f4f6f9', border: '1px solid var(--border)', borderRadius: 8,
                  padding: 12, marginBottom: entry.is_non_working ? 0 : 16
                }}>
                  <label className="radio-option" style={{ margin: 0, alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={!!entry.is_non_working}
                      onChange={e => toggleNonWorking(idx, e.target.checked)}
                      style={{ marginTop: 3 }} />
                    <span style={{ marginLeft: 8 }}>
                      <strong>This was a non-working period.</strong>
                      <div className="field-hint" style={{ marginTop: 4 }}>
                        Check this for time you were not employed — unemployment, disability,
                        medical leave, personal time, etc. You'll just tell us what it was and
                        when it started.
                      </div>
                    </span>
                  </label>
                </div>
              )}

              {entry.is_school ? (
                // Warn ONLY when this entry is what's blocking. A driver whose
                // dated history already covers the span is fine even though the
                // terminator isn't honoured -- don't alarm him over nothing.
                !schoolRejected ? (
                  <div style={{ padding: '4px 0', fontSize: 14, color: '#276749' }}>
                    ✅ School, ending {formatMonthYear(entry.end_date)} — no further details
                    needed. Your employment history is complete.
                  </div>
                ) : (
                  // The end date is derived, not typed, so it must be shown: it is
                  // now the thing the entry is judged on.
                  <div style={{ padding: '4px 0', fontSize: 14, color: '#9b2c2c' }}>
                    ⚠️ School, ending {formatMonthYear(entry.end_date)}
                    {schoolAgeAtEnd !== null && ` (age ${schoolAgeAtEnd})`}. Your history must
                    reach back to {formatMonthYear(requiredBackTo)}, so school cannot be the
                    start of it. Please tell us what you were doing before then — and if you
                    were in school or training during that time, uncheck this box and use
                    <strong> "This was a non-working period"</strong> instead, which lets you
                    enter the dates and keep going further back.
                  </div>
                )
              ) : entry.is_non_working ? (
              <>
              <div className="field-group">
                <label className="field-label">What was this? <span className="required">*</span></label>
                <input className={`field-input ${err.reason ? 'error' : ''}`} value={entry.reason}
                  onChange={e => setField(idx, 'reason', e.target.value)}
                  placeholder='e.g. "Unemployment", "Medical leave", "Disability"' />
                {err.reason && <div className="field-error">{err.reason}</div>}
              </div>

              <div className="field-row">
                <div className="field-group">
                  <label className="field-label">Start Date <span className="required">*</span></label>
                  <input type="month" className={`field-input ${err.start_date ? 'error' : ''}`} value={entry.start_date}
                    onChange={e => setField(idx, 'start_date', e.target.value)} />
                  {err.start_date && <div className="field-error">{err.start_date}</div>}
                </div>
                <div className="field-group">
                  <label className="field-label">End Date <span className="field-hint">(auto)</span></label>
                  <input type="month" className="field-input" value={entry.end_date}
                    disabled style={{ background: '#f4f6f9', color: 'var(--text-light)' }} />
                  {err.end_date && <div className="field-error">{err.end_date}</div>}
                </div>
              </div>
              </>
              ) : (
              <>
              <div className="field-group">
                <label className="field-label">Employer / Organization Name <span className="required">*</span></label>
                <input className={`field-input ${err.employer_name ? 'error' : ''}`} value={entry.employer_name}
                  onChange={e => setField(idx, 'employer_name', e.target.value)}
                  placeholder='e.g. "ABC Trucking" or "Self-Employed" or "Unemployed"' />
                {err.employer_name && <div className="field-error">{err.employer_name}</div>}
              </div>

              <div className="field-group">
                <label className="field-label">Street Address <span className="field-hint">(optional)</span></label>
                <input className="field-input" value={entry.street}
                  onChange={e => setField(idx, 'street', e.target.value)} placeholder="Street address (optional)" />
              </div>

              <div className="field-row">
                <div className="field-group">
                  <label className="field-label">City <span className="required">*</span></label>
                  <input className={`field-input ${err.city ? 'error' : ''}`} value={entry.city}
                    onChange={e => setField(idx, 'city', e.target.value)} placeholder="City" />
                  {err.city && <div className="field-error">{err.city}</div>}
                </div>
                <div className="field-group">
                  <label className="field-label">State <span className="required">*</span></label>
                  <select className={`field-select ${err.state ? 'error' : ''}`} value={entry.state}
                    onChange={e => setField(idx, 'state', e.target.value)}>
                    <option value="">Select...</option>
                    {US_STATES.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                  </select>
                  {err.state && <div className="field-error">{err.state}</div>}
                </div>
              </div>

              <div className="field-group">
                <label className="field-label">ZIP Code <span className="field-hint">(optional)</span></label>
                <input className="field-input" value={entry.zip}
                  onChange={e => setField(idx, 'zip', e.target.value)} placeholder="ZIP (optional)" />
              </div>

              <div className="field-row">
                <div className="field-group">
                  <label className="field-label">Start Date <span className="required">*</span></label>
                  <input type="month" className={`field-input ${err.start_date ? 'error' : ''}`} value={entry.start_date}
                    onChange={e => setField(idx, 'start_date', e.target.value)} />
                  {err.start_date && <div className="field-error">{err.start_date}</div>}
                </div>
                <div className="field-group">
                  <label className="field-label">End Date <span className="field-hint">{isFirst ? '(current)' : '(auto)'}</span></label>
                  {isFirst ? (
                    <input type="month" className="field-input" value={entry.end_date} disabled style={{ background: '#f4f6f9', color: 'var(--text-light)' }} />
                  ) : (
                    <input type="month" className={`field-input ${err.end_date ? 'error' : ''}`} value={entry.end_date}
                      disabled style={{ background: '#f4f6f9', color: 'var(--text-light)' }} />
                  )}
                  {err.end_date && <div className="field-error">{err.end_date}</div>}
                </div>
              </div>

              <div className="field-group">
                <label className="field-label">Position Held <span className="required">*</span></label>
                <input className={`field-input ${err.position ? 'error' : ''}`} value={entry.position}
                  onChange={e => setField(idx, 'position', e.target.value)}
                  placeholder='e.g. "OTR Driver" or "Unemployed" or "Medical Leave"' />
                {err.position && <div className="field-error">{err.position}</div>}
              </div>

              <div className="field-group">
                <label className="field-label">DOT Regulated Position? <span className="required">*</span></label>
                <div className="radio-row">
                  <label className="radio-option">
                    <input type="radio" name={`dot_${idx}`} checked={entry.dot_regulated === true}
                      onChange={() => setField(idx, 'dot_regulated', true)} /> Yes — DOT
                  </label>
                  <label className="radio-option">
                    <input type="radio" name={`dot_${idx}`} checked={entry.dot_regulated === false}
                      onChange={() => setField(idx, 'dot_regulated', false)} /> No — Non-DOT
                  </label>
                </div>
                {err.dot_regulated && <div className="field-error">{err.dot_regulated}</div>}
              </div>
              </>
              )}
            </div>
          </div>
        );
      })}

      <div className="step-card">
        {/* Once a school entry exists the history is terminated -- nothing earlier
            can ever be required, so there is nothing left to add. */}
        {!hasSchoolEntry && (
          <button className="btn-back" style={{ width: '100%', marginBottom: 8 }} onClick={addEntry}>
            + Add Another Entry
          </button>
        )}
        {!historySatisfied && earliestStart && !schoolRejected && (
          <div className="field-hint" style={{ textAlign: 'center', marginBottom: 12 }}>
            Keep adding entries back to {formatMonthYear(requiredBackTo)} — or add a final entry and
            check <strong>"Before this, I was in school"</strong> if you weren't working that whole time.
          </div>
        )}
        {/* Pointing a rejected driver back at the school box would loop him. */}
        {schoolRejected && (
          <div className="field-hint" style={{ textAlign: 'center', marginBottom: 12 }}>
            Uncheck <strong>"Before this, I was in school"</strong> on the entry above to continue
            your history back to {formatMonthYear(requiredBackTo)}.
          </div>
        )}
        <div className="wizard-nav" style={{ position: 'static', borderTop: 'none', padding: 0, marginTop: 8 }}>
          <button className="btn-back" onClick={onBack}>← Back</button>
          <button className="btn-next" onClick={next}>Next →</button>
        </div>
      </div>
    </div>
  );
}
